/**
 * Darknet-Tab – Crime Ops (/business/crime-ops, Tab DARKNET, NT-DARKNET-01).
 *
 * Order-Board (W2-getimte Rohware-Nachfrage, Teillieferung, Beschlagnahme/Undercover-
 * Risiko) + Shop (Saatgut/Trank-Zutaten/OpSec/Papiere) + E-soft Premium-Festpreis-Ankauf.
 * Gate: Talent "Darknet-Zugang" (darknet_zugang) — Backend liefert 403 DARKNET_ACCESS_REQUIRED
 * ohne das Talent, diese Komponente zeigt in dem Fall nur einen Hinweistext.
 */

import { api, ApiError } from '../../services/api.js';
import { showToast } from '../../utils/toast.js';
import { refreshBalance } from '../../utils/refreshBalance.js';
import { formatPropertyDisplayName, renderPropertyOptions } from '../../utils/property-select.js';

// ─── Typen (spiegeln Backend-Views) ────────────────────────────────────────────

type DarknetOrderStatus =
  | 'OPEN' | 'ACCEPTED' | 'FULFILLED' | 'PARTIALLY_FULFILLED'
  | 'EXPIRED' | 'CANCELLED' | 'UNDERCOVER_CAUGHT';

interface DarknetOrderView {
  id: string;
  strainKey: string;
  strainLabel: string;
  qualityTier: number;
  totalQuantity: number;
  deliveredQuantity: number;
  remainingQuantity: number;
  rewardCents: string;
  status: DarknetOrderStatus;
  windowSeconds: number | null;
  acceptedAt: string | null;
  windowEndsAt: string | null;
  flavorHints: string[];
  markerShown: boolean;
  shipmentsUsed: number;
}

interface DarknetBoardView {
  orders: DarknetOrderView[];
  reputation: number;
  maxOpenOrders: number;
}

interface DarknetShopItemView {
  key: string;
  label: string;
  description: string;
  category: 'SEED' | 'TRANK_ZUTAT' | 'OPSEC' | 'PAPIERE';
  priceCents: string;
  unitLabel: string;
  purchaseLimit: number | null;
  limitPeriodDays: number;
  wantedLevelLock?: number;
  lore: string;
  effectImplemented?: boolean;
  boughtInPeriod: number;
  remainingInPeriod: number | null;
}

interface OwnedWarehouse {
  id: string;
  label: string;
  customName: string | null;
  nickname: string | null;
  slots: number;
  currentLoad: number;
  inventoryItems: { itemType: string; itemKey: string; quantity: number }[];
}

interface ShipResult {
  order: DarknetOrderView;
  outcome: 'DELIVERED' | 'SEIZED' | 'UNDERCOVER_CAUGHT';
  shippedQuantity: number;
}

const CATEGORY_LABELS: Record<DarknetShopItemView['category'], string> = {
  SEED: 'EXOTISCHES SAATGUT',
  TRANK_ZUTAT: 'HELDENTRANK-ZUTATEN',
  OPSEC: 'OPSEC-GÜTER',
  PAPIERE: 'HEISSE PAPIERE',
};

const OPSEC_ITEM_KEYS = ['vakuumbeutel', 'fake_absender', 'mixer'];

// Premium-Festpreis-Ankauf-Eligibilität (spiegelt isDarknetPremiumEligible() im Backend,
// kein Shared-Import für reine Anzeige-Werte, etabliertes Pattern) — nur Grow-Top-Tier
// (Q5) der zwei exotischen Sorten + Heldentrank (immer eligible, konstant REINWARE).
const PREMIUM_ELIGIBLE_LABELS: Record<string, string> = {
  silberfell_q5: 'Silberfell (Q5, Premium-Ernte)',
  schwarzwald_diesel_q5: 'Schwarzwald-Diesel (Q5, Premium-Ernte)',
  heldentrank_q5: 'Heldentrank',
};

// ─── State ────────────────────────────────────────────────────────────────────

let isMounted = false;
let accessDenied = false;
let board: DarknetBoardView | null = null;
let shopItems: DarknetShopItemView[] = [];
let warehouses: OwnedWarehouse[] = [];
// Verstecke (Hideouts) — Silberfell/Schwarzwald-Diesel wachsen im Gewächshaus-Slot eines
// Verstecks, die Ernte landet dort im Inventar. Für den Premium-Verkauf muss auch dort
// nach Bestand gesucht werden, nicht nur in Lagerhäusern.
let hideouts: OwnedWarehouse[] = [];
let selectedWarehouseId = '';
let shipQtyByOrder: Record<string, number> = {};
let shipOpsecByOrder: Record<string, Set<string>> = {};
let reloadPromise: Promise<void> | null = null;
let boardTimer: ReturnType<typeof setInterval> | null = null;
let clickHandler: ((e: Event) => void) | null = null;
let changeHandler: ((e: Event) => void) | null = null;

// ─── Styles ───────────────────────────────────────────────────────────────────

function addDarknetStyles(): void {
  const id = 'darknet-tab-styles';
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = `
    #dn-root { font-family: var(--font-mono); }
    .dn-header {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: var(--space-4); padding-bottom: var(--space-2);
      border-bottom: 1px solid var(--border);
    }
    .dn-rep { font-size: var(--font-size-lg); color: var(--accent-purple); }
    .dn-rep-label { font-size: var(--font-size-xs); color: var(--text-muted); text-transform: uppercase; }
    .dn-section-title {
      font-size: var(--font-size-base); text-transform: uppercase; letter-spacing: 0.08em;
      margin: var(--space-4) 0 var(--space-2) 0; color: var(--text-primary);
    }
    .dn-empty { color: var(--text-muted); font-size: var(--font-size-sm); padding: var(--space-3) 0; }
    .dn-order-card {
      border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-3);
      margin-bottom: var(--space-3); background: var(--bg-surface);
    }
    .dn-order-card--marked { border-color: var(--accent-orange); }
    .dn-order-title { font-size: var(--font-size-sm); color: var(--text-primary); margin-bottom: var(--space-1); }
    .dn-order-meta { font-size: var(--font-size-xs); color: var(--text-muted); margin-bottom: var(--space-2); }
    .dn-badge { display: inline-block; padding: 2px 6px; border-radius: var(--radius-sm); font-size: var(--font-size-xs); text-transform: uppercase; margin-right: var(--space-1); }
    .dn-badge--marker { background: var(--accent-orange); color: var(--bg-base); }
    .dn-hint { font-size: var(--font-size-xs); color: var(--accent-orange); font-style: italic; margin: 2px 0; }
    .dn-order-actions { display: flex; gap: var(--space-2); flex-wrap: wrap; align-items: center; margin-top: var(--space-2); }
    .dn-qty-input { width: 4rem; background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-primary); padding: 4px; font-family: var(--font-mono); font-size: var(--font-size-xs); text-align: center; -moz-appearance: textfield; }
    .dn-qty-input::-webkit-outer-spin-button, .dn-qty-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    .dn-qty-row { display: flex; align-items: center; gap: var(--space-1); }
    .dn-qty-btn { font-family: var(--font-mono); font-size: var(--font-size-xs); height: 28px; min-width: 28px; border: 1px solid var(--border); background: var(--bg-surface); color: var(--text-primary); border-radius: var(--radius-btn); cursor: pointer; padding: 0; touch-action: manipulation; }
    .dn-qty-btn:hover { border-color: var(--text-muted); }
    .dn-qty-btn--minus { border-color: var(--accent-red); color: var(--accent-red); }
    .dn-qty-btn--minus:hover { background: color-mix(in srgb, var(--accent-red) 12%, transparent); }
    .dn-qty-btn--plus { border-color: var(--accent-green); color: var(--accent-green); }
    .dn-qty-btn--plus:hover { background: color-mix(in srgb, var(--accent-green) 12%, transparent); }
    .dn-qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .dn-opsec-row { display: flex; gap: var(--space-2); font-size: var(--font-size-xs); color: var(--text-muted); }
    .dn-shop-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: var(--space-3); }
    .dn-shop-item { border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-3); background: var(--bg-surface); }
    .dn-shop-item--depleted { opacity: 0.5; }
    .dn-shop-item-label { font-size: var(--font-size-sm); color: var(--text-primary); }
    .dn-shop-item-desc { font-size: var(--font-size-xs); color: var(--text-muted); margin: var(--space-1) 0; line-height: 1.4; }
    .dn-shop-item-noeffect { font-size: var(--font-size-xs); color: var(--accent-red); border: 1px solid var(--accent-red); border-radius: var(--radius-sm); padding: var(--space-1) var(--space-2); margin: var(--space-1) 0; line-height: 1.4; }
    .dn-shop-item-price { font-size: var(--font-size-xs); color: var(--accent-gold); margin: var(--space-1) 0; }
    .dn-shop-item-limit { font-size: var(--font-size-xs); color: var(--text-muted); }
    .dn-warehouse-select { background: var(--bg-elevated); border: 1px solid var(--border); color: var(--text-primary); padding: 6px; font-family: var(--font-mono); font-size: var(--font-size-xs); margin-bottom: var(--space-3); }
    .dn-premium-form { display: flex; gap: var(--space-2); flex-wrap: wrap; align-items: center; border: 1px solid var(--border); border-radius: var(--radius); padding: var(--space-3); }
    .dn-err { color: var(--accent-red); font-size: var(--font-size-xs); margin-top: var(--space-2); display: none; }
    #dn-countdown-list span { font-variant-numeric: tabular-nums; }
  `;
}

// ─── Hilfsfunktionen ────────────────────────────────────────────────────────

function fmtBlackMoney(cents: string): string {
  return `${(Number(cents) / 100).toFixed(2)} S-₦`;
}

function fmtCountdown(iso: string | null): string {
  if (!iso) return '–';
  const remaining = new Date(iso).getTime() - Date.now();
  if (remaining <= 0) return 'ABGELAUFEN';
  const totalSeconds = Math.floor(remaining / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return h > 0 ? `${String(h)}h ${String(m)}m` : `${String(m)}m ${String(s)}s`;
}

function statusLabel(status: DarknetOrderStatus): string {
  return ({
    OPEN: 'OFFEN', ACCEPTED: 'ANGENOMMEN', FULFILLED: 'ERFÜLLT',
    PARTIALLY_FULFILLED: 'TEILWEISE ERFÜLLT', EXPIRED: 'ABGELAUFEN',
    CANCELLED: 'STORNIERT', UNDERCOVER_CAUGHT: 'AUFGEFLOGEN',
  } as Record<DarknetOrderStatus, string>)[status];
}

// ─── Render ───────────────────────────────────────────────────────────────────

function renderOrderCard(order: DarknetOrderView): string {
  const cardClass = order.markerShown ? 'dn-order-card dn-order-card--marked' : 'dn-order-card';
  const hints = order.flavorHints.map((h) => `<div class="dn-hint">⚠ ${h}</div>`).join('');
  const markerBadge = order.markerShown ? '<span class="dn-badge dn-badge--marker">VERDÄCHTIG</span>' : '';

  let actions = '';
  if (order.status === 'OPEN') {
    actions = `<div class="dn-order-actions">
      <button class="btn btn--primary btn--small" data-dn-accept="${order.id}">ANNEHMEN</button>
    </div>`;
  } else if (order.status === 'ACCEPTED') {
    const qty = shipQtyByOrder[order.id] ?? Math.max(1, Math.min(order.remainingQuantity, Math.ceil(order.totalQuantity * 0.25)));
    const chosenOpsec = shipOpsecByOrder[order.id] ?? new Set<string>();
    const opsecCheckboxes = OPSEC_ITEM_KEYS.map((key) => `
      <label><input type="checkbox" data-dn-opsec="${order.id}" data-dn-opsec-key="${key}" ${chosenOpsec.has(key) ? 'checked' : ''}> ${key}</label>
    `).join('');

    actions = `
      <div class="dn-order-meta">Fenster: <span data-dn-countdown="${order.id}">${fmtCountdown(order.windowEndsAt)}</span> · Chargen: ${String(order.shipmentsUsed)}/4</div>
      <div class="dn-order-actions">
        <input type="number" class="dn-qty-input" min="1" max="${String(order.remainingQuantity)}" value="${String(qty)}" data-dn-qty="${order.id}">
        <button class="btn btn--primary btn--small" data-dn-ship="${order.id}">SENDEN</button>
        <button class="btn btn--secondary btn--small" data-dn-express="${order.id}">EXPRESS (Scheine)</button>
        <button class="btn btn--secondary btn--small" data-dn-cancel="${order.id}">RÜCKZIEHER (Scheine)</button>
      </div>
      <div class="dn-opsec-row">${opsecCheckboxes}</div>
    `;
  }

  return `<div class="${cardClass}">
    <div class="dn-order-title">${markerBadge}${order.strainLabel} Q${String(order.qualityTier)} — ${String(order.totalQuantity)} Einheiten</div>
    <div class="dn-order-meta">
      Status: ${statusLabel(order.status)} · Geliefert: ${String(order.deliveredQuantity)}/${String(order.totalQuantity)} ·
      Belohnung: ${fmtBlackMoney(order.rewardCents)}
    </div>
    ${hints}
    ${actions}
    <div class="dn-err" id="dn-err-${order.id}"></div>
  </div>`;
}

function renderBoard(): string {
  if (!board) return '<p class="dn-empty">LADE...</p>';
  const orders = board.orders;
  return `
    <div class="dn-header">
      <div><span class="dn-rep-label">REPUTATION</span><br><span class="dn-rep">${String(board.reputation)}</span></div>
      <div><span class="dn-rep-label">OFFENE ORDERS</span><br><span class="dn-rep">${String(orders.length)}/${String(board.maxOpenOrders)}</span></div>
    </div>
    ${orders.length === 0
      ? '<p class="dn-empty">Keine Orders auf dem Board. Kontakte melden sich unregelmäßig — Knappheit ist Absicht.</p>'
      : orders.map(renderOrderCard).join('')}
  `;
}

function renderWarehouseSelect(): string {
  if (warehouses.length === 0) return '<p class="dn-empty">Kein Lager vorhanden.</p>';
  // NT-POLISH-01 (P1): zentrale Immobilien-Auswahl (Eigenname — Typ (X frei)).
  const options = renderPropertyOptions(
    warehouses.map((w) => ({ id: w.id, label: w.label, customName: w.customName, nickname: w.nickname, isHideout: false, freeSlots: w.slots - w.currentLoad })),
    { selectedId: selectedWarehouseId },
  );
  return `<select class="dn-warehouse-select" data-dn-warehouse>${options}</select>`;
}

function renderShop(): string {
  if (shopItems.length === 0) return '<p class="dn-empty">LADE...</p>';

  const categories: DarknetShopItemView['category'][] = ['SEED', 'TRANK_ZUTAT', 'OPSEC', 'PAPIERE'];
  const grouped = categories.map((cat) => {
    const items = shopItems.filter((i) => i.category === cat);
    if (items.length === 0) return '';
    return `
      <div class="dn-section-title">${CATEGORY_LABELS[cat]}</div>
      <div class="dn-shop-grid">
        ${items.map((item) => {
          const depleted = item.remainingInPeriod !== null && item.remainingInPeriod <= 0;
          return `<div class="dn-shop-item${depleted ? ' dn-shop-item--depleted' : ''}">
            <div class="dn-shop-item-label">${item.label}</div>
            <div class="dn-shop-item-desc">${esc(item.description)}</div>
            ${item.effectImplemented === false ? '<div class="dn-shop-item-noeffect">NOCH OHNE WIRKUNG — Effekt ist nicht verdrahtet, das Item landet nur im Lager.</div>' : ''}
            <div class="dn-shop-item-price">${fmtBlackMoney(item.priceCents)} / ${item.unitLabel}</div>
            <div class="dn-shop-item-limit">${item.remainingInPeriod === null ? '' : `Noch ${String(item.remainingInPeriod)} verfügbar (${String(item.limitPeriodDays)}T)`}</div>
            <div class="dn-order-actions">
              <div class="dn-qty-row">
                <button class="dn-qty-btn dn-qty-btn--minus" data-dn-buy-qty-step="${item.key}" data-dir="-1" ${depleted ? 'disabled' : ''}>-</button>
                <input type="number" class="dn-qty-input" min="1" max="${String(item.remainingInPeriod ?? 99)}" value="1" data-dn-buy-qty="${item.key}">
                <button class="dn-qty-btn dn-qty-btn--plus" data-dn-buy-qty-step="${item.key}" data-dir="1" ${depleted ? 'disabled' : ''}>+</button>
              </div>
              <button class="btn btn--primary btn--small" data-dn-buy="${item.key}" ${depleted ? 'disabled' : ''}>KAUFEN</button>
            </div>
          </div>`;
        }).join('')}
      </div>
    `;
  }).join('');

  return renderWarehouseSelect() + grouped;
}

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

interface PremiumStockEntry { propertyId: string; propertyLabel: string; itemKey: string; quantity: number }

function getPremiumStock(): PremiumStockEntry[] {
  const entries: PremiumStockEntry[] = [];
  for (const w of [...warehouses, ...hideouts]) {
    for (const inv of w.inventoryItems) {
      if (inv.quantity <= 0 || !(inv.itemKey in PREMIUM_ELIGIBLE_LABELS)) continue;
      entries.push({
        propertyId: w.id,
        propertyLabel: formatPropertyDisplayName({ id: w.id, label: w.label, customName: w.customName, nickname: w.nickname, isHideout: false }),
        itemKey: inv.itemKey,
        quantity: inv.quantity,
      });
    }
  }
  return entries;
}

function renderPremiumSell(): string {
  const stock = getPremiumStock();

  const formHtml = stock.length === 0
    ? '<p class="dn-empty">Keine Premium-Ware auf Lager (Silberfell/Schwarzwald-Diesel Q5 oder Heldentrank).</p>'
    : `<div class="dn-premium-form">
        <select class="dn-warehouse-select" data-dn-premium-select style="margin-bottom:0;">
          ${stock.map((s) => `<option value="${esc(s.propertyId)}::${esc(s.itemKey)}::${String(s.quantity)}">
            ${esc(PREMIUM_ELIGIBLE_LABELS[s.itemKey] ?? s.itemKey)} (${String(s.quantity)} STK.) — ${esc(s.propertyLabel)}
          </option>`).join('')}
        </select>
        <input type="number" class="dn-qty-input" min="1" value="1" data-dn-premium-qty>
        <button class="btn btn--primary btn--small" data-dn-premium-sell>VERKAUFEN</button>
      </div>`;

  return `
    <div class="dn-section-title">E-SOFT — PREMIUM-FESTPREIS-ANKAUF</div>
    <p class="dn-empty">Nur Top-Qualität: Silberfell/Schwarzwald-Diesel Q5 oder Heldentrank. Zahlt deutlich mehr als der Hehler.</p>
    ${formHtml}
    <div class="dn-err" id="dn-premium-err"></div>
  `;
}

function renderContent(): string {
  if (accessDenied) {
    return '<p class="dn-empty">Darknet-Zugang benötigt das Talent "Darknet-Zugang" (KÄPSELE-Baum, Tier 2).</p>';
  }
  return `
    <div class="dn-section-title">ORDER-BOARD</div>
    ${renderBoard()}
    <div class="dn-section-title">DARKNET-SHOP</div>
    ${renderShop()}
    ${renderPremiumSell()}
  `;
}

export function renderDarknetTab(): string {
  addDarknetStyles();
  return `<div id="dn-root">${renderContent()}</div>`;
}

// ─── Modal (Sendungs-Ergebnis: Beschlagnahme / Undercover) ───────────────────

function showShipResultModal(result: ShipResult): void {
  const existing = document.getElementById('dn-result-modal');
  if (existing) existing.remove();

  let title: string;
  let body: string;
  if (result.outcome === 'UNDERCOVER_CAUGHT') {
    title = 'UNDERCOVER-FALLE';
    body = 'Die Order war eine Falle. Die Ware ist weg, kein Geld, deine Fahndung ist gestiegen. Reputation im Darknet gesunken.';
  } else if (result.outcome === 'SEIZED') {
    title = 'SENDUNG ABGEFANGEN';
    body = 'Diese Charge wurde am Zoll abgefangen. Die Order bleibt offen — du kannst bis Fensterende nachliefern.';
  } else {
    title = 'SENDUNG ANGEKOMMEN';
    body = 'Die Ware ist durchgekommen.';
  }

  const el = document.createElement('div');
  el.id = 'dn-result-modal';
  el.className = 'modal-backdrop';
  el.innerHTML = `
    <div class="modal modal--narrow">
      <div class="modal-header"><h2>${title}</h2></div>
      <div class="modal-body"><p>${body}</p></div>
      <div class="modal-footer"><button class="btn btn--primary" id="dn-result-ok">OK</button></div>
    </div>
  `;
  document.body.appendChild(el);
  document.getElementById('dn-result-ok')?.addEventListener('click', () => {
    el.remove();
  });
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function loadAll(): Promise<void> {
  try {
    const [boardRes, shopRes, propsRes] = await Promise.all([
      api.get<DarknetBoardView>('/darknet/orders'),
      api.get<{ items: DarknetShopItemView[] }>('/darknet/shop'),
      api.get<{ warehouses: OwnedWarehouse[]; hideouts: OwnedWarehouse[] }>('/properties'),
    ]);
    board = boardRes;
    shopItems = shopRes.items;
    warehouses = propsRes.warehouses;
    hideouts = propsRes.hideouts;
    if (!selectedWarehouseId && warehouses[0]) selectedWarehouseId = warehouses[0].id;
    accessDenied = false;
  } catch (err) {
    if (err instanceof ApiError && err.code === 'DARKNET_ACCESS_REQUIRED') {
      accessDenied = true;
    } else {
      throw err;
    }
  }
}

async function reload(): Promise<void> {
  if (reloadPromise) { await reloadPromise; return; }
  reloadPromise = (async () => {
    try {
      await loadAll();
    } finally {
      reloadPromise = null;
    }
    rerender();
  })();
  await reloadPromise;
}

function rerender(): void {
  const root = document.getElementById('dn-root');
  if (!root) return;
  addDarknetStyles();
  root.innerHTML = renderContent();
}

function startCountdownTimer(): void {
  if (boardTimer) { clearInterval(boardTimer); boardTimer = null; }
  boardTimer = setInterval(() => {
    if (!board) return;
    let anyExpired = false;
    for (const order of board.orders) {
      if (order.status !== 'ACCEPTED') continue;
      const el = document.querySelector(`[data-dn-countdown="${order.id}"]`);
      if (!el) continue;
      const remaining = order.windowEndsAt ? new Date(order.windowEndsAt).getTime() - Date.now() : -1;
      if (remaining <= 0) anyExpired = true;
      el.textContent = fmtCountdown(order.windowEndsAt);
    }
    if (anyExpired) void reload();
  }, 1000);
}

// ─── Event-Listener ───────────────────────────────────────────────────────────

function showOrderError(orderId: string, message: string): void {
  const el = document.getElementById(`dn-err-${orderId}`);
  if (el) { el.textContent = message; el.style.display = 'block'; }
}

function attachListeners(): void {
  const root = document.getElementById('dn-root');
  if (!root) return;

  clickHandler = (e: Event) => {
    const target = e.target as Element;

    const acceptBtn = target.closest<HTMLButtonElement>('[data-dn-accept]');
    if (acceptBtn?.dataset.dnAccept) {
      void handleAccept(acceptBtn.dataset.dnAccept);
      return;
    }

    const shipBtn = target.closest<HTMLButtonElement>('[data-dn-ship]');
    if (shipBtn?.dataset.dnShip) {
      void handleShip(shipBtn.dataset.dnShip);
      return;
    }

    const expressBtn = target.closest<HTMLButtonElement>('[data-dn-express]');
    if (expressBtn?.dataset.dnExpress) {
      void handleExpress(expressBtn.dataset.dnExpress);
      return;
    }

    const cancelBtn = target.closest<HTMLButtonElement>('[data-dn-cancel]');
    if (cancelBtn?.dataset.dnCancel) {
      void handleCancel(cancelBtn.dataset.dnCancel);
      return;
    }

    const buyQtyStepBtn = target.closest<HTMLButtonElement>('[data-dn-buy-qty-step]');
    if (buyQtyStepBtn?.dataset.dnBuyQtyStep) {
      const itemKey = buyQtyStepBtn.dataset.dnBuyQtyStep;
      const dir = parseInt(buyQtyStepBtn.dataset.dir ?? '0', 10);
      const input = document.querySelector<HTMLInputElement>(`[data-dn-buy-qty="${itemKey}"]`);
      if (input) {
        const max = parseInt(input.max, 10) || 99;
        const current = parseInt(input.value, 10) || 1;
        input.value = String(Math.max(1, Math.min(max, current + dir)));
      }
      return;
    }

    const buyBtn = target.closest<HTMLButtonElement>('[data-dn-buy]');
    if (buyBtn?.dataset.dnBuy) {
      void handleBuy(buyBtn.dataset.dnBuy);
      return;
    }

    const premiumSellBtn = target.closest<HTMLButtonElement>('[data-dn-premium-sell]');
    if (premiumSellBtn) {
      void handlePremiumSell();
      return;
    }
  };

  changeHandler = (e: Event) => {
    const target = e.target as HTMLInputElement;

    if (target.dataset.dnQty !== undefined) {
      shipQtyByOrder = { ...shipQtyByOrder, [target.dataset.dnQty]: parseInt(target.value, 10) || 1 };
      return;
    }
    if (target.dataset.dnOpsec !== undefined && target.dataset.dnOpsecKey !== undefined) {
      const orderId = target.dataset.dnOpsec;
      const key = target.dataset.dnOpsecKey;
      const current = new Set(shipOpsecByOrder[orderId] ?? []);
      if (target.checked) current.add(key); else current.delete(key);
      shipOpsecByOrder = { ...shipOpsecByOrder, [orderId]: current };
      return;
    }
    if (target.dataset.dnWarehouse !== undefined || target.closest('[data-dn-warehouse]')) {
      selectedWarehouseId = (target as unknown as HTMLSelectElement).value;
      return;
    }
  };

  root.addEventListener('click', clickHandler);
  root.addEventListener('change', changeHandler);
}

function detachListeners(): void {
  const root = document.getElementById('dn-root');
  if (!root || !clickHandler || !changeHandler) return;
  root.removeEventListener('click', clickHandler);
  root.removeEventListener('change', changeHandler);
  clickHandler = null;
  changeHandler = null;
}

// ─── API-Aktionen ─────────────────────────────────────────────────────────────

async function handleAccept(orderId: string): Promise<void> {
  try {
    await api.post(`/darknet/orders/${encodeURIComponent(orderId)}/accept`, {});
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Annahme fehlgeschlagen.', 'error');
  }
}

async function handleShip(orderId: string): Promise<void> {
  const order = board?.orders.find((o) => o.id === orderId);
  const quantity = shipQtyByOrder[orderId] ?? order?.remainingQuantity ?? 1;
  const opsecItemKeys = [...(shipOpsecByOrder[orderId] ?? [])];
  try {
    const result = await api.post<ShipResult>(`/darknet/orders/${encodeURIComponent(orderId)}/ship`, { quantity, opsecItemKeys });
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await reload();
    showShipResultModal(result);
  } catch (err) {
    showOrderError(orderId, err instanceof ApiError ? err.message : 'Sendung fehlgeschlagen.');
  }
}

async function handleExpress(orderId: string): Promise<void> {
  try {
    await api.post(`/darknet/orders/${encodeURIComponent(orderId)}/express`, {});
    void refreshBalance(); // NT-HOTFIX-01 (H6): Scheine
    await reload();
  } catch (err) {
    showOrderError(orderId, err instanceof ApiError ? err.message : 'Express fehlgeschlagen.');
  }
}

async function handleCancel(orderId: string): Promise<void> {
  try {
    await api.post(`/darknet/orders/${encodeURIComponent(orderId)}/cancel`, {});
    void refreshBalance(); // NT-HOTFIX-01 (H6): Scheine
    await reload();
  } catch (err) {
    showOrderError(orderId, err instanceof ApiError ? err.message : 'Rückzieher fehlgeschlagen.');
  }
}

async function handleBuy(itemKey: string): Promise<void> {
  if (!selectedWarehouseId) { showToast('Kein Lager ausgewählt.', 'error'); return; }
  const qtyInput = document.querySelector<HTMLInputElement>(`[data-dn-buy-qty="${itemKey}"]`);
  const quantity = parseInt(qtyInput?.value ?? '1', 10) || 1;
  try {
    await api.post('/darknet/shop/buy', { itemKey, quantity, targetPropertyId: selectedWarehouseId });
    showToast('Gekauft.');
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Kauf fehlgeschlagen.', 'error');
  }
}

async function handlePremiumSell(): Promise<void> {
  const errEl = document.getElementById('dn-premium-err');
  if (errEl) errEl.style.display = 'none';
  const selectEl = document.querySelector<HTMLSelectElement>('[data-dn-premium-select]');
  const qtyInput = document.querySelector<HTMLInputElement>('[data-dn-premium-qty]');
  if (!selectEl?.value) return;

  const [sourcePropertyId, itemKey, maxQtyRaw] = selectEl.value.split('::');
  if (!sourcePropertyId || !itemKey) return;
  const maxQty = parseInt(maxQtyRaw ?? '1', 10) || 1;
  const quantity = Math.max(1, Math.min(maxQty, parseInt(qtyInput?.value ?? '1', 10) || 1));

  try {
    await api.post('/darknet/premium-sell', { itemKey, quantity, sourcePropertyId });
    showToast('Verkauft.');
    void refreshBalance(); // NT-HOTFIX-01 (H6)
    await reload();
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Verkauf fehlgeschlagen.';
    if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; }
  }
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

export function mountDarknetTab(): void {
  if (isMounted) return;
  isMounted = true;
  attachListeners();
  void reload().then(() => { startCountdownTimer(); });
}

export async function refreshDarknetTab(): Promise<void> {
  if (!isMounted) return;
  await reload();
  startCountdownTimer();
}

export function cleanupDarknetTab(): void {
  if (boardTimer) { clearInterval(boardTimer); boardTimer = null; }
  detachListeners();
  document.getElementById('dn-result-modal')?.remove();
  board = null;
  shopItems = [];
  warehouses = [];
  hideouts = [];
  selectedWarehouseId = '';
  shipQtyByOrder = {};
  shipOpsecByOrder = {};
  accessDenied = false;
  isMounted = false;
}
